import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { usePoll } from '../../hooks/usePoll';
import { fetchFanTokens } from '../../utils';
import * as actionTypes from '../../store/actionTypes';
import CommonMenu from '../../components/commonMenu/commonMenu';
import TokenTable from '../../components/Tables/TokenTable/token.table';
import Transactions from '../Transactions/transaction.table';
import MarketStats from '../../components/MarketStats/MarketStats';
import { useTokenListing, ListingFooter } from '../../hooks/useTokenListing';

// Nexus tokens (formerly FanTokens) listing.
//
// Rebuilt on the shared TokenTable like VentureTokens: same columns (live
// OBD, sparkline, Swap, Edit), price flash, and a search box that actually
// searches - the old hand-rolled table ignored it. fan_tokens rows use
// token_* column names, so they're mapped into TokenTable's shape.
const toRow = (each) => ({
  ...each,
  identitySymbol: each.token_symbol,
  symbol: each.token_symbol,
  name: each.token_name,
  logo: each.image,
  description: each.token_desc,
  currentPrice: Number(each.token_price) || 0,
  price1hour: Number(each.price1hour) || Number(each.token_price) || 0,
  price24hour: Number(each.price24hour) || Number(each.token_price) || 0,
  volume: Number(each.volume) || 0,
  href: 'nexus'
});

const FanTable = () => {
  const dispatch = useDispatch();
  const { fanTokens } = useSelector(
    (state) => ({ fanTokens: state.fanTokens }),
    shallowEqual
  );

  const [searchToken, setSearchToken] = useState('');
  const [prevPrices, setPrevPrices] = useState({});
  const [blinkUpList, setBlinkUpList] = useState([]);
  const [blinkDownList, setBlinkDownList] = useState([]);

  usePoll(() => fetchFanTokens(dispatch, actionTypes.FETCH_FAN_TOKENS), 5000, []);

  const rows = useMemo(
    () => (Array.isArray(fanTokens) ? fanTokens : []).map(toRow),
    [fanTokens]
  );

  const listing = useTokenListing(rows, searchToken);

  useEffect(() => {
    const next = {};
    const up = [];
    const down = [];
    for (const r of rows) {
      next[r.identitySymbol] = r.currentPrice;
      const prev = prevPrices[r.identitySymbol];
      if (prev !== undefined && prev !== r.currentPrice) {
        (r.currentPrice > prev ? up : down).push(r.identitySymbol);
      }
    }
    setPrevPrices(next);
    if (up.length || down.length) {
      setBlinkUpList(up);
      setBlinkDownList(down);
      const t = setTimeout(() => {
        setBlinkUpList([]);
        setBlinkDownList([]);
      }, 500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  return (
    <div
      className="screen"
      style={{
        zIndex: '100',
        position: 'sticky',
        width: '100%',
        marginBottom: '1rem'
      }}
    >
      <div style={{ width: '95%', minWidth: '340px' }} className="res-token">
        <MarketStats />
        <CommonMenu label={'Nexus'} setSearch={setSearchToken} />
        <TokenTable
          loading={false}
          token={listing.pageRows}
          blinkUpList={blinkUpList}
          blinkDownList={blinkDownList}
          href={'nexus'}
          swappable={true}
          showPecu={false}
        />
        <ListingFooter
          listing={listing}
          loaded={fanTokens?.length > 0}
          noun="Nexus tokens"
        />
        <Transactions />
      </div>
    </div>
  );
};

export default React.memo(FanTable);
